// src/components/toggle/Toggle.tsx

import { useState, useEffect, useRef, ReactNode } from 'react';
import './Toggle.scss';

interface ToggleProps {
  defaultValue?: string | boolean | number;
  values?: any[];
  labels?: string[];
  icons?: ReactNode[];
  onChange?: (index: number, value: any) => void;
  buttonStyle?: 'regular' | 'flush';
  spacing?: number;
}

export function Toggle({
  defaultValue = 0,
  values = [],
  labels = [],
  icons,
  onChange = () => { },
  buttonStyle = 'regular',
  spacing = 8,
}: ToggleProps) {
  // Convert defaultValue to index
  const getIndexFromValue = (value: string | boolean | number): number => {
    if (typeof value === 'boolean') {
      return value ? 1 : 0;
    } else if (typeof value === 'string') {
      return Math.max(0, values.indexOf(value));
    } else if (typeof value === 'number') {
      return value;
    }
    return 0;
  };

  const [activeIndex, setActiveIndex] = useState<number>(getIndexFromValue(defaultValue));
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const bgRef = useRef<HTMLDivElement>(null);

  const items = labels.length > 0 ? labels : values;

  // Watch for defaultValue changes
  useEffect(() => {
    const newIndex = getIndexFromValue(defaultValue);
    if (newIndex !== activeIndex) {
      setActiveIndex(newIndex);
      onChange(newIndex, values[newIndex] || '');
    }
  }, [defaultValue, values, onChange, activeIndex]);

  const handleClick = (index: number) => {
    if (index === activeIndex) {
      return;
    }
    setActiveIndex(index);
    onChange(index, values[index] || '');
  };

  useEffect(() => {
    const bgEl = bgRef.current;
    const activeEl = itemRefs.current[activeIndex];

    if (bgEl && activeEl) {
      bgEl.style.left = `${activeEl.offsetLeft}px`;
      bgEl.style.width = `${activeEl.offsetWidth}px`;
    }
  }, [activeIndex]);

  const renderItems = () => {
    return items.map((item, index) => {
      const elements = [];

      if (index > 0 && buttonStyle === 'flush') {
        elements.push(
          <div key={`divider-${index}`} className="divider" />
        );
      }

      elements.push(
        <div
          key={`label-${index}`}
          className={`label ${index === items.length - 1 ? 'last' : ''}`}
          ref={(el) => (itemRefs.current[index] = el)}
          onClick={() => handleClick(index)}
          data-active={activeIndex === index}
          title={item?.toString()}
        >
          {icons ? icons[index] : item}
        </div>
      );

      return elements;
    }).flat();
  };

  return (
    <div
      data-component="Toggle"
      data-style={buttonStyle}
      style={{ '--toggle-spacing': `${spacing}px` } as React.CSSProperties}
    >
      {renderItems()}
      {buttonStyle === 'regular' && <div className="toggle-background" ref={bgRef} />}
    </div>
  );
}